import { useEffect, useMemo } from 'react';
import type { RouteDefinition } from '@/routes';
import { AppLayout } from '@/layout/AppLayout';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { applyRouteMeta } from '@/lib/meta';
import { legalArticles } from '@/content/legal-articles';

type Props = {
  route: RouteDefinition;
};

function normalizeLegalHref(rawHref: string): string {
  const href = rawHref.trim();

  if (!href || href.startsWith('#') || href.startsWith('//') || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) {
    return href;
  }

  const match = href.match(/^([^?#]+)([?#].*)?$/);
  const pathPart = match?.[1] ?? href;
  const suffix = match?.[2] ?? '';

  let normalizedPath = pathPart.replace(/^(\.\.\/|\.\/)+/, '');

  if (!normalizedPath.startsWith('/')) {
    normalizedPath = `/${normalizedPath}`;
  }

  if (normalizedPath === '/index.html' || normalizedPath === '/index') {
    return '/';
  }

  if (normalizedPath.endsWith('.html')) {
    normalizedPath = normalizedPath.slice(0, -'.html'.length);
  }

  return `${normalizedPath}${suffix}`;
}

function stripComments(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments: Comment[] = [];

  while (walker.nextNode()) {
    comments.push(walker.currentNode as Comment);
  }

  for (const comment of comments) {
    comment.remove();
  }
}

function sanitizeLegalMarkup(markup: string): string {
  if (typeof DOMParser === 'undefined') {
    return markup;
  }

  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const root = doc.body;
  stripComments(root);

  const firstHeading = root.querySelector(':scope > h1');
  if (firstHeading) {
    firstHeading.remove();
  }

  const firstParagraph = root.querySelector(':scope > p');
  if (firstParagraph && /last updated/i.test(firstParagraph.textContent ?? '')) {
    firstParagraph.remove();
  }

  const nodes: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];

  for (const node of nodes) {
    const tagName = node.tagName.toLowerCase();

    if (tagName === 'script' || tagName === 'style') {
      node.remove();
      continue;
    }

    for (const { name } of Array.from(node.attributes)) {
      if (name === 'class' || name === 'style' || name.startsWith('on')) {
        node.removeAttribute(name);
      }
    }

    if (tagName === 'a') {
      const href = node.getAttribute('href');
      if (!href) {
        continue;
      }

      node.setAttribute('href', normalizeLegalHref(href));

      if (/^https?:\/\//i.test(href)) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noreferrer');
      }
    }
  }

  return root.innerHTML.trim();
}

export function LegalPage({ route }: Props) {
  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const article = legalArticles[route.canonicalPath];
  const bodyHtml = useMemo(
    () => (article ? sanitizeLegalMarkup(article.bodyHtml) : ''),
    [article]
  );

  if (!article) {
    return null;
  }

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{article.eyebrow}</p>
        <h1 className="mt-2 font-display text-4xl leading-tight md:text-5xl">{article.title}</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">{article.intro}</p>
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Last updated: {article.lastUpdated}
        </p>
      </section>

      <section className="brand-frame rounded-2xl border border-border bg-card/92 p-6 md:p-8">
        <article
          className="space-y-5 text-sm text-muted-foreground [&_a]:text-foreground [&_a]:underline [&_h2]:mt-6 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-foreground [&_h3]:mt-4 [&_h3]:font-semibold [&_h3]:text-foreground [&_h4]:mt-3 [&_h4]:font-semibold [&_h4]:text-foreground [&_li]:ml-5 [&_li]:list-disc [&_p]:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
    </AppLayout>
  );
}
